import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["viewport", "prev", "next", "current", "total"]

  static values = {
    fade: { type: Boolean, default: false },
    loop: { type: Boolean, default: true },
    autoplay: { type: Boolean, default: true },
    autoplayDelay: { type: Number, default: 5000 },
    align: { type: String, default: "center" }
  }

  // Embla is loaded on connect so it stays out of the bundle every page downloads.
  async connect () {
    const connection = (this.connection = Symbol("connection"))
    if (!this.hasViewportTarget) return

    this.slides = [...this.viewportTarget.querySelectorAll(".item")]
    if (!this.slides.length) return

    this.slides.forEach((slide) => slide.classList.add("slider__slide"))

    const [{ default: EmblaCarousel }, plugins] = await Promise.all([import("embla-carousel"), this.loadPlugins()])
    if (this.connection !== connection) return // disconnected (or reconnected) while loading

    this.embla = EmblaCarousel(this.viewportTarget, this.emblaOptions(), this.emblaPlugins(plugins))
    this.embla.on("select", () => this.sync())
    this.embla.on("reInit", () => this.sync())
    this.sync()
  }

  disconnect () {
    this.connection = null
    this.embla?.destroy()
    this.embla = null
  }

  prev () {
    this.embla?.scrollPrev()
  }

  next () {
    this.embla?.scrollNext()
  }

  emblaOptions () {
    return {
      loop: this.loopValue,
      align: this.alignValue,
      containScroll: this.loopValue ? false : "trimSnaps",
      duration: 25
    }
  }

  async loadPlugins () {
    const [{ default: ClassNames }, { default: Fade }, { default: Autoplay }] = await Promise.all([
      import("embla-carousel-class-names"),
      import("embla-carousel-fade"),
      import("embla-carousel-autoplay")
    ])
    return { ClassNames, Fade, Autoplay }
  }

  emblaPlugins ({ ClassNames, Fade, Autoplay }) {
    const plugins = [ClassNames()]
    if (this.fadeValue) plugins.push(Fade())
    if (this.autoplayValue) {
      plugins.push(Autoplay({
        delay: this.autoplayDelayValue,
        stopOnInteraction: false,
        stopOnMouseEnter: true
      }))
    }
    return plugins
  }

  sync () {
    if (!this.embla) return

    if (this.hasCurrentTarget) {
      this.currentTarget.textContent = String(this.embla.selectedScrollSnap() + 1)
    }
    if (this.hasTotalTarget) {
      this.totalTarget.textContent = String(this.embla.slideNodes().length)
    }

    if (this.loopValue) return
    if (this.hasPrevTarget) {
      this.prevTarget.classList.toggle("slider__arrow--disabled", !this.embla.canScrollPrev())
    }
    if (this.hasNextTarget) {
      this.nextTarget.classList.toggle("slider__arrow--disabled", !this.embla.canScrollNext())
    }
  }
}
